import { Calendar, Video, FileText, User, PlayCircle, Loader, Clock } from 'lucide-react';
import { useState, useEffect } from 'react';

const PastTraining = () => {
  const [pastTrainings, setPastTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch past training programs from API
  useEffect(() => {
    const fetchPastTraining = async () => {
      const cachedData = localStorage.getItem("cachedPastTrainings_v2");
      const cacheTimestamp = localStorage.getItem("pastTrainingsTimestamp_v2");
      const now = Date.now();

      if (cachedData && cacheTimestamp && now - parseInt(cacheTimestamp) < 3600000) {
        setPastTrainings(JSON.parse(cachedData));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch("https://admin.torchbearer.co.ke/api/training-programs/past");

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          const parsedPrograms = data.data.map(program => {
            let parsedFeatures = [];
            if (program.features) {
              try {
                parsedFeatures = typeof program.features === 'string'
                  ? JSON.parse(program.features)
                  : Array.isArray(program.features)
                    ? program.features
                    : [];
              } catch (e) {
                console.error(`Failed to parse features for program ${program.id}:`, e);
                parsedFeatures = [];
              }
            }
            return {
              ...program,
              features: parsedFeatures,
            };
          });

          setPastTrainings(parsedPrograms);
          localStorage.setItem("cachedPastTrainings_v2", JSON.stringify(parsedPrograms));
          localStorage.setItem("pastTrainingsTimestamp_v2", now.toString());
        } else {
          throw new Error(data.message || "Failed to fetch training programs");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPastTraining();
  }, []);

  // Format date range
  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'Date TBA';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;

    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };

    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString('en-US', options);
    }

    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.getDate()}, ${start.getFullYear()}`;
    }

    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };

  // Extract YouTube video ID
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Helper to extract YouTube Thumbnail
  const getYoutubeThumbnail = (url) => {
    const videoId = getYoutubeVideoId(url);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-24">
            <Loader className="w-16 h-16 animate-spin mx-auto text-amber-500 mb-6" />
            <p className="text-slate-600 text-lg font-semibold">Loading training archive...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-2 border-red-200 rounded-3xl p-16 text-center max-w-2xl mx-auto">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-red-600 text-4xl font-bold">!</span>
            </div>
            <p className="text-red-900 text-2xl font-bold mb-3">Connection Error</p>
            <p className="text-red-700 mb-8">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-red-700 transition-all"
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-32 pb-20 md:pt-48 md:pb-32 bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 bg-amber-100 text-amber-800 px-5 py-2 rounded-full text-sm font-bold mb-6">
            <Video className="w-4 h-4" />
            Training Archive
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
            Past <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-600">Recordings</span>
          </h2>
          <p className="text-slate-600 text-xl max-w-3xl mx-auto leading-relaxed">
            Access our complete library of professional training sessions and masterclasses
          </p>
        </div>

        {pastTrainings.length === 0 ? (
          <div className="bg-white rounded-3xl border-2 border-slate-200 p-20 text-center shadow-sm">
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Calendar className="w-12 h-12 text-slate-300" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">No recordings available yet</h3>
            <p className="text-slate-600 max-w-md mx-auto text-lg">
              Past training sessions will appear here after they're completed and processed.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pastTrainings.map((training) => {
              const youtubeVideoId = getYoutubeVideoId(training.recording_url);
              const youtubeThumb = getYoutubeThumbnail(training.recording_url);
              const displayImage = youtubeThumb || training.image_url || '/images/default-training-past.png';

              return (
                <div key={training.id} className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-amber-400 transition-all duration-300 hover:shadow-xl hover:shadow-amber-50/50 flex flex-col">
                  {/* Video Preview Section */}
                  <div className="relative aspect-video overflow-hidden bg-slate-900">
                    {youtubeVideoId ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                        title={training.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <>
                        <img
                          src={displayImage}
                          alt={training.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />

                        {!training.recording_url && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-slate-800/80 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-xs font-semibold">
                              No Recording
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-slate-900 px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-lg flex items-center gap-1.5">
                      <Calendar className="w-3 h-3 text-amber-600" />
                      {formatDateRange(training.start_date, training.end_date)}
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-5 flex-grow flex flex-col">
                    <h3 className="text-lg font-bold text-slate-900 mb-3 line-clamp-2 leading-snug group-hover:text-amber-600 transition-colors h-12">
                      {training.title}
                    </h3>

                    {training.speaker && (
                      <div className="flex items-center gap-2 mb-4 bg-slate-50 w-fit px-3 py-1.5 rounded-lg">
                        <User className="w-3.5 h-3.5 text-amber-500" />
                        <span className="text-xs font-bold text-slate-600 truncate max-w-[150px]">{training.speaker}</span>
                      </div>
                    )}

                    <p className="text-slate-600 text-sm mb-5 line-clamp-2 leading-relaxed h-10">
                      {training.description || "Professional training session covering technical modules."}
                    </p>

                    {/* Features Tags */}
                    {training.features && training.features.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-5">
                        {training.features.slice(0, 2).map((feature, i) => (
                          <span key={i} className="text-[10px] bg-amber-50 text-amber-700 px-2 py-1 rounded-md font-bold border border-amber-100 uppercase tracking-tight">
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-auto flex gap-2 pt-4 border-t border-slate-50">
                      {training.recording_url && !youtubeVideoId && (
                        <a
                          href={training.recording_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1.5 bg-slate-900 text-white px-3 py-2.5 rounded-lg text-xs font-bold hover:bg-amber-600 transition-all"
                        >
                          <PlayCircle className="w-4 h-4" />
                          Watch
                        </a>
                      )}

                      {training.slides_url && (
                        <a
                          href={training.slides_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${training.recording_url && !youtubeVideoId ? 'flex-1' : 'w-full'} flex items-center justify-center gap-1.5 bg-white border border-slate-900 text-slate-900 px-3 py-2.5 rounded-lg text-xs font-bold hover:bg-slate-50 transition-all`}
                        >
                          <FileText className="w-4 h-4" />
                          Slides
                        </a>
                      )}

                      {!training.slides_url && !training.recording_url && (
                        <div className="w-full text-center py-2 text-slate-400 text-xs font-bold">
                          Archived
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default PastTraining;